import { LoadingCommon, SessionYearMonthSelect, TableList } from '@components';
import { images } from '@constants';
import { ArrowLeftIcon } from '@constants/svgs';
import { Button, Form } from 'antd';
import { useCallback, useMemo, useRef } from 'react';
import { QuantityEditDialog } from './components/QuantityEditDialog';
import { SendConfirmDialog } from './components/SendConfirmDialog';
import { getColumns } from './columns';
import {
  FORM_NAME,
  HEIGHT_TABLE_SCROLL,
  MANAGEMENT_SEAL_REQUEST_CONSTANT,
} from './constant';
import useManagementSealRequest from './hooks/useManagementSealRequest';
import { ManagementSealRequestWrapper } from './ManagementSealRequestStyled';

const { TITLE, MESSAGE, LABEL, FILTER_LABEL } =
  MANAGEMENT_SEAL_REQUEST_CONSTANT;

const FORM_ID = 'management-seal-request-form';

function ManagementSealRequest() {
  const tableRef = useRef(null);
  const { state, handler } = useManagementSealRequest();
  const {
    form,
    rows,
    loadingPage,
    // loadingOther, // 会社/店舗 filter tạm ẩn — bật lại khi có yêu cầu hiển thị
    loadingExport,
    // companiesSelect, // 会社 filter tạm ẩn
    // storesSelect, // 店舗 filter tạm ẩn
    sessionsSelect,
    selectedRow,
    // selectedRowKeys, // checkbox selection UI tạm ẩn
    quantityEditOpen,
    sendConfirmOpen,
    newQuantity,
    quantityError,
    loadingConfirm,
  } = state;

  const handleScroll = useCallback(() => {
    return;
  }, []);

  // 年月 [filter] hiển thị qua SessionYearMonthSelect (Năm/Tháng) nhưng giá trị gửi API vẫn là
  // sessionCode (không phải inventoryYearMonth) — field SESSION_CODE được giữ đăng ký qua
  // Form.Item hidden bên dưới, cập nhật thủ công qua form.setFieldValue khi đổi Năm/Tháng.
  const sessionCode = Form.useWatch(FORM_NAME.SESSION_CODE, form);
  const selectedYearMonth = useMemo(
    () =>
      sessionsSelect.find((session) => session.sessionCode === sessionCode)
        ?.inventoryYearMonth,
    [sessionsSelect, sessionCode]
  );

  const handleChangeYearMonth = useCallback(
    (yearMonth: string) => {
      const matched = sessionsSelect.find(
        (session) => session.inventoryYearMonth === yearMonth
      );
      form.setFieldValue(FORM_NAME.SESSION_CODE, matched?.sessionCode);
    },
    [form, sessionsSelect]
  );

  const columns = useMemo(() => {
    return getColumns({
      handleOpenQuantityEdit: handler.handleOpenQuantityEdit,
    });
  }, [handler.handleOpenQuantityEdit]);

  // checkbox selection UI tạm ẩn — bật lại cùng với rowKeyField/rowSelection bên dưới khi cần.
  // const rowSelection = useMemo(
  //   () => ({
  //     type: 'checkbox' as const,
  //     columnWidth: 60,
  //     selectedRowKeys,
  //     onChange: handler.handleRowSelectionChange,
  //   }),
  //   [selectedRowKeys, handler.handleRowSelectionChange]
  // );

  return (
    <LoadingCommon loading={loadingPage}>
      <ManagementSealRequestWrapper listdata={rows.length}>
        <div className="management-seal-request_header">
          <div className="management-seal-request_header-title">
            <button onClick={handler.handleNavigateBack}>
              <ArrowLeftIcon />
            </button>
            <h1>{TITLE.MANAGEMENT_SEAL_REQUEST}</h1>
          </div>

          <div className="management-seal-request_header-btn">
            <Button
              loading={loadingExport}
              disabled={rows.length === 0}
              onClick={handler.handleExportData}
            >
              {TITLE.BTN_EXPORT}
            </Button>
          </div>
        </div>

        <div className="management-seal-request_form">
          <Form
            id={FORM_ID}
            layout="vertical"
            form={form}
            onFinish={handler.handleSearch}
          >
            <div className="management-seal-request_form-filters-row">
              <Form.Item name={FORM_NAME.SESSION_CODE} hidden>
                <input type="hidden" />
              </Form.Item>
              <Form.Item
                label={FILTER_LABEL.SESSION_CODE}
                className="management-seal-request_form-session"
              >
                <SessionYearMonthSelect
                  sessions={sessionsSelect}
                  value={selectedYearMonth}
                  onChange={handleChangeYearMonth}
                />
              </Form.Item>

              {/* 会社/店舗 filter tạm ẩn — chưa có yêu cầu hiển thị, bật lại khi cần.
              <Form.Item
                name={FORM_NAME.COMPANY_CODE}
                label={FILTER_LABEL.COMPANY}
                className="management-seal-request_form-company"
              >
                <Select
                  size="large"
                  loading={loadingOther}
                  options={companiesSelect}
                  suffixIcon={<ArrowSelect />}
                />
              </Form.Item>

              <Form.Item
                name={FORM_NAME.STORE_ID}
                label={FILTER_LABEL.STORE}
                className="management-seal-request_form-store"
              >
                <Select
                  size="large"
                  loading={loadingOther}
                  options={storesSelect}
                  suffixIcon={<ArrowSelect />}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
              */}

              <Button
                className="management-seal-request_search-btn"
                htmlType="submit"
                form={FORM_ID}
                autoInsertSpace={false}
              >
                {TITLE.BTN_SEARCH}
              </Button>
            </div>
          </Form>
        </div>

        <TableList
          tableRef={tableRef}
          image={images.noResult}
          columns={columns}
          handleScroll={handleScroll}
          heightTableScroll={HEIGHT_TABLE_SCROLL}
          listData={rows}
          messageNoData={MESSAGE.NO_DATA}
          rowKey={null}
          // rowKeyField="id" // checkbox selection UI tạm ẩn
          // rowSelection={rowSelection} // checkbox selection UI tạm ẩn
          borderColor="var(--color-border-primary)"
        />

        <p className="management-seal-request_total">
          {LABEL.TOTAL_RECORDS_PREFIX}
          <span>{rows.length}</span>
          {LABEL.TOTAL_RECORDS_SUFFIX}
        </p>

        {/* VAMS_PC_008_01 数量編集ダイアログ */}
        <QuantityEditDialog
          open={quantityEditOpen}
          row={selectedRow}
          newQuantity={newQuantity}
          quantityError={quantityError}
          onChangeQuantity={handler.handleChangeQuantity}
          onCancel={handler.handleCloseQuantityEdit}
          onSubmit={handler.handleSubmitQuantity}
        />

        {/* VAMS_PC_008_02 送信確認ダイアログ — nested on top of Dialog D */}
        <SendConfirmDialog
          open={sendConfirmOpen}
          loading={loadingConfirm}
          row={selectedRow}
          newQuantity={newQuantity}
          onCancel={handler.handleCloseSendConfirm}
          onOk={handler.handleConfirmSend}
        />
      </ManagementSealRequestWrapper>
    </LoadingCommon>
  );
}

export { ManagementSealRequest };
